import './TaskItem.scss'

import {
  ChangeEvent,
  createRef,
  KeyboardEvent,
  useEffect,
  useState,
} from 'react'
import styled from 'styled-components'
import { AnimateLayoutChanges, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import {
  taskDeleted,
  taskModified,
  TaskPayload,
  taskToggled,
} from './tasks-slice'
import { useAppDispatch, useAppSelector, useDidMount } from '../../app/hooks'

import { CheckBoxInput, TextAreaInput } from '../../common/components'
import { ReorderIcon } from '../../common/components/icons'

/**
 * A delay in the dispatch function, when a task is opened.
 * Kept short so rapid multi-checking isn't blocked by the animation.
 */
const DISPATCH_OPENED_DELAY_MS = 250

/**
 * A delay in the dispatch function, when a task is completed.
 * Kept short so rapid multi-checking isn't blocked by the animation.
 */
const DISPATCH_COMPLETED_DELAY_MS = 400

const alwaysAnimateLayoutChanges: AnimateLayoutChanges = () => true

const Container = styled.div<{ completed?: boolean }>`
  align-content: center;
  align-items: center;
  display: flex;
  flex-direction: row;

  ${({ completed }) =>
    completed &&
    `
    color: var(--sn-stylekit-info-color);
  `}

  min-width: 10%;
  max-width: 85%;
`

export type TaskItemProps = {
  task: TaskPayload
  groupName: string
  innerRef?: React.Ref<HTMLDivElement>
}

const TaskItem: React.FC<TaskItemProps> = ({
  task,
  groupName,
  innerRef,
  ...props
}) => {
  const textAreaRef = createRef<HTMLTextAreaElement>()

  const dispatch = useAppDispatch()

  const canEdit = useAppSelector((state) => state.settings.canEdit)
  const spellCheckEnabled = useAppSelector(
    (state) => state.settings.spellCheckerEnabled
  )

  const [completed, setCompleted] = useState(!!task.completed)
  const [description, setDescription] = useState(task.description)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging
  } = useSortable({ id: task.id, disabled: !canEdit, animateLayoutChanges: alwaysAnimateLayoutChanges })

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition: 'transform 200ms cubic-bezier(0.25, 1, 0.5, 1)',
    ...(isDragging ? { opacity: 0.5, position: 'relative', zIndex: 1000 } as React.CSSProperties : {}),
  }

  function resizeTextArea(textarea: HTMLTextAreaElement | null): void {
    if (!textarea) {
      return
    }

    /**
     * Set to 1px first to reset scroll height in case it shrunk.
     */
    const heightOffset = 4
    textarea.style.height = '1px'
    textarea.style.height = textarea.scrollHeight - heightOffset + 'px'
  }

  useEffect(() => {
    resizeTextArea(textAreaRef.current)
  })

  function onCheckBoxToggle() {
    const newCompletedState = !completed
    setCompleted(newCompletedState)

    newCompletedState
      ? textAreaRef.current!.classList.add('cross-out')
      : textAreaRef.current!.classList.add('no-text-decoration')

    const dispatchDelay = newCompletedState
      ? DISPATCH_COMPLETED_DELAY_MS
      : DISPATCH_OPENED_DELAY_MS

    setTimeout(() => {
      dispatch(taskToggled({ id: task.id, groupName }))
    }, dispatchDelay)
  }

  function onTextChange(event: ChangeEvent<HTMLTextAreaElement>) {
    setDescription(event.target.value)
  }

  function onKeyUp(event: KeyboardEvent<HTMLTextAreaElement>) {
    // Delete task if empty and enter pressed
    if (event.key === 'Enter') {
      if (description.length === 0) {
        dispatch(taskDeleted({ id: task.id, groupName }))
        event.preventDefault()
      }
    }

    const element = event.target as HTMLTextAreaElement
    resizeTextArea(element)
  }

  function onKeyPress(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter') {
      // We want to disable any action on enter.
      event.preventDefault()
    }
  }

  /**
   * Save the task after the user has stopped typing.
   */
  useDidMount(() => {
    const timeoutId = setTimeout(() => {
      if (description !== task.description) {
        dispatch(
          taskModified({ task: { id: task.id, description }, groupName })
        )
      }
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [description, groupName])

  return (
    <div ref={setNodeRef} style={sortableStyle} className="task-item">
      <Container
        data-testid="task-item"
        completed={completed}
        ref={innerRef}
        {...props}
      >
        {canEdit && (
          <div className="mr-3 cursor-grab" {...attributes} {...listeners}>
            <ReorderIcon highlight={isDragging} />
          </div>
        )}
        <CheckBoxInput
          testId="check-box-input"
          checked={completed}
          disabled={!canEdit}
          onChange={onCheckBoxToggle}
        />
        <TextAreaInput
          testId="text-area-input"
          className="text-area-input"
          disabled={!canEdit || !!completed}
          onChange={onTextChange}
          onKeyPress={onKeyPress}
          onKeyUp={onKeyUp}
          ref={textAreaRef}
          spellCheck={spellCheckEnabled}
          value={description}
        />
      </Container>
    </div>
  )
}

export default TaskItem
